// frontend/src/utils/sessionMarkdown.js
// Generates markdown representation of a shared session

const SAFE_PROTOCOLS = ['http:', 'https:', 'ftp:', 'mailto:'];

/**
 * Escape markdown special characters in inline text.
 */
function escapeMd(text) {
  if (!text) return '';
  return text.replace(/([\\`*_{}\[\]()#+\-.!|>~])/g, '\\$1');
}

/**
 * Sanitize a URL for safe use inside markdown link syntax.
 * Blocks dangerous protocols and encodes chars that break [text](url).
 */
function sanitizeUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (!SAFE_PROTOCOLS.includes(parsed.protocol)) return '';
  } catch {
    // relative or malformed — pass through (encode only)
  }
  return url.replace(/ /g, '%20').replace(/\)/g, '%29').replace(/\(/g, '%28');
}

/**
 * Render a single tab as a markdown list item.
 * Format: `N. ![](favicon) **[Title](url)** — description`
 */
function renderTab(tab, index) {
  const title = escapeMd(tab.title || 'Untitled');
  const url = sanitizeUrl(tab.url || '');
  const hostname = tab.hostname || '';
  const faviconMd = hostname
    ? `![](https://www.google.com/s2/favicons?domain=${hostname}&sz=16) `
    : '';
  const line = `${index + 1}. ${faviconMd}**[${title}](${url})**`;
  if (tab.description && tab.description.trim() !== (tab.title || '').trim()) {
    return `${line} — ${escapeMd(tab.description)}`;
  }
  return line;
}

/**
 * Generate full markdown for a shared session.
 *
 * @param {Object} session - Session object (name, description, tags, sections)
 * @param {Array} tabs - Array of tab objects
 * @param {Object|null} owner - Owner object (username, displayName)
 * @param {Object} options - { skipHeader: false }
 * @returns {string} Markdown string
 */
export function generateMarkdown(session, tabs, owner, options = {}) {
  const { skipHeader = false } = options;
  const lines = [];

  if (!skipHeader) {
    // Title
    lines.push(`# ${escapeMd(session.name || 'Untitled Session')}`);
    lines.push('');

    // Description — escape each line and prefix with > (skip if identical to title)
    if (session.description && session.description.trim() !== (session.name || '').trim()) {
      const descLines = session.description.split('\n').map(l => `> ${escapeMd(l)}`);
      lines.push(...descLines);
      lines.push('');
    }
  }

  // Tags
  if (session.tags?.length > 0) {
    lines.push(`*Tags: ${session.tags.map(t => escapeMd(t)).join(', ')}*`);
    lines.push('');
  }

  const hasSections = session.sections?.length > 0;

  if (hasSections) {
    // Build section map sorted by order
    const sections = [...session.sections].sort((a, b) => a.order - b.order);
    const sectionTabMap = {};
    for (const sec of sections) {
      sectionTabMap[sec.id] = [];
    }
    const unsectioned = [];

    for (const tab of tabs) {
      if (tab.sectionId && sectionTabMap[tab.sectionId]) {
        sectionTabMap[tab.sectionId].push(tab);
      } else {
        unsectioned.push(tab);
      }
    }

    // Render each section with tabs
    for (const sec of sections) {
      const sectionTabs = sectionTabMap[sec.id];
      if (sectionTabs.length === 0) continue;

      lines.push(`## ${escapeMd(sec.title || 'Untitled Section')}`);
      lines.push('');
      sectionTabs.forEach((tab, i) => lines.push(renderTab(tab, i)));
      lines.push('');
    }

    // Unsectioned tabs
    if (unsectioned.length > 0) {
      lines.push('## Other');
      lines.push('');
      unsectioned.forEach((tab, i) => lines.push(renderTab(tab, i)));
      lines.push('');
    }
  } else {
    // Flat list
    tabs.forEach((tab, i) => lines.push(renderTab(tab, i)));
    lines.push('');
  }

  // Footer attribution
  if (owner?.username) {
    lines.push('---');
    lines.push(`*Curated by @${escapeMd(owner.username)} on [Blackmount](https://app.blackmount.ai)*`);
    lines.push('');
  }

  return lines.join('\n').trim() + '\n';
}
